import React, {
  useState, useEffect, useCallback, useRef,
} from 'react';
import {
  Label, Icon, Popup, Button, Grid,
} from 'semantic-ui-react';

import toast from 'react-hot-toast';
import api from 'api';
import styled from 'styled-components';
import { connect } from 'react-redux';
import Participant from './Participant';
import * as UI from './UI';
import { getVideo } from './video';

const ActionsContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2px;
  ${({ minimised }) => !minimised && `
    position: absolute;
    right: 0px;
    left: 0px;
    bottom: 5px;
  `}
`;

const MaximiseIcon = styled(Icon)`
  cursor: pointer;
  color: white;
  margin-bottom: 10px !important;
  `;

const HeaderDiv = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-bottom: 2px;
    padding: 3px;
  `;

const HeaderButton = styled(Button)`
    background-color: black !important;
    color: white !important;
    display: contents;
    padding: 0 !important;
    margin: 0 !important;
    cursor: pointer;
  `;

const UserAvatar = styled.div`
position: absolute;
background-color:white;
border-radius:50% !important;
height:  80px;
width: 80px;
right:0;
left:0;
bottom: 0;
top:0;
margin: auto;
  background-color: #184258fc;
  > span {
    display: block;
    line-height: 80px;
    color: white;
    font-weight: bold;
    text-align: center;
    font-size: 18px;
}
`;
const GridColumn = styled(Grid.Column)`
height: 100% ;
margin-bottom: 10px !important;
  `;

const PlaceholderContainer = styled.div`
    background-color: black;
    border-radius: 4px;
    margin-top: 5px;
    margin-bottom: 5px;
    margin: auto;
    height:  inherit;
    `;

const VideoGrid = styled(Grid)`
  ${({ minimised }) => (!minimised) && `
    height: 83vh;
    width: 100%;
  `}
  `;

const SharingColumn = styled(Grid.Column)`
  height: 100%;
  width: 100%;
  display: ${({ sharing }) => !sharing && 'none'};
  `;

const ShareVideo = styled.video`
   display: ${({ sharing }) => !sharing && 'none'};
   height: inherit !important;
   width: inherit !important;
  `;

const Room = ({
  roomName, token, handleLogout, user, localTracks, videoControls,
}) => {
  const [room, setRoom] = useState(null);
  const [participantList, setParticipantList] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [sharing, setSharing] = useState(false);
  const [screenShare, setScreenShare] = useState(null);
  const videoRefShare = useRef();
  const [audioStats, setAudioStats] = useState(null);
  const [dominantSpeaker, setDominantSpeaker] = useState(null);
  const [localNetworkQuality, setLocalNetworkQuality] = useState(null);
  const [localConnected, setLocalConnected] = useState(true);
  const userId = user._id;
  const {
    muted, setMuted, cameraOff, setCameraOff, minimised, setMinimised,
  } = videoControls;

  const leaveRoom = useCallback(() => {
    if (room) {
      room.localParticipant.videoTracks.forEach((publication) => {
        publication.track.stop();
        room.localParticipant.unpublishTrack(publication.track);
        room.disconnect();
      });
    }
    handleLogout();
    setRoom(null);
  }, [room, handleLogout]);

  useEffect(() => {
    if (localTracks) {
      getVideo()
        .then((Video) => {
          Video.connect(token, {
            name: roomName,
            dominantSpeaker: true,
            tracks: [localTracks?.audio, localTracks?.video],
            networkQuality: true,
            //  logLevel: 'debug'
          })
            .then((newRoom) => setRoom(newRoom))
            .catch(() => toast.error('Unable to join room'));
      });
    }
  }, [roomName, token, localTracks]);

  useEffect(() => {
    if (!room) return;
    room.on('participantConnected', (participant) => {
      if (participant.identity === userId) {
        room.localParticipant.videoTracks.forEach((publication) => {
          publication.track.stop();
          room.localParticipant.unpublishTrack(publication.track);
          room.disconnect();
        });
      } else {
        // We don't want duplicate participants! Get rid!
        setParticipants((prevParticipants) => {
          const filteredParticipants = prevParticipants.filter((p) => p.identity !== participant.identity);
          return [...filteredParticipants, participant];
        });
        api.users.getVideoParticipant(participant.identity, (u) => {
          setParticipantList((prevParticipants) => {
            const filteredParticipants = prevParticipants.filter((p) => p._id !== u._id);
            return [...filteredParticipants, u];
          });
          toast(`${u.profile.fullName} joined the call`);
        });
      }
    });
  }, [userId, room]);

  useEffect(() => {
    if (!room) return;
    room.on('participantDisconnected', (participant) => {
      setParticipants((prevParticipants) => prevParticipants.filter((p) => p !== participant));
      setParticipantList((prevParticipantList) => {
        const existingParticipant = prevParticipantList.find((p) => p._id === participant.identity);
        toast(`${existingParticipant.profile.fullName} left the call`);
        return prevParticipantList.filter((p) => p._id !== participant.identity);
      });
    });
    room.on('reconnecting', () => {
      setLocalConnected(false);
    });
    room.on('reconnected', () => {
      setLocalConnected(true);
    });
  }, [room]);

  useEffect(() => {
    if (!room) return;
    room.on('dominantSpeakerChanged', (participant) => setDominantSpeaker(participant));
  }, [room]);

  useEffect(() => {
    if (!room) return;
    room.participants.forEach((participant) => {
      setParticipants((prevParticipants) => [...prevParticipants, participant]);
      api.users.getVideoParticipant(participant.identity, (u) => { setParticipantList((prevParticipantList) => [...prevParticipantList, u]); });
    });
  }, [room]);

  useEffect(() => {
    window.addEventListener('beforeunload', leaveRoom);
    return () => {
      document.removeEventListener('beforeunload', leaveRoom);
    };
  }, [leaveRoom]);

  const shareScreen = useCallback(() => {
    if (!room) return;
    if (sharing) {
      if (sharing !== user._id) {
        toast.error('Only one user can share their screen at any time');
        return;
      }
      room.localParticipant.unpublishTrack(screenShare);
      screenShare.stop();
      setScreenShare(null);
      setSharing(false);
    } else {
      navigator.mediaDevices.getDisplayMedia().then((stream) => {
        const screenTrack = stream.getVideoTracks()[0];
        room.localParticipant.publishTrack(screenTrack, { name: 'screenShare', priority: 'low' });
        setSharing(room.localParticipant.identity);
        setScreenShare(screenTrack);
        screenTrack.onended = () => {
          setSharing(false);
          setScreenShare(null);
          room.localParticipant.videoTracks.forEach((publication) => {
            if (publication.track.name === 'screenShare' && publication.track.isStopped === true) {
              room.localParticipant.unpublishTrack(publication.track);
              publication.track.stop();
            }
          });
        };
      }).catch(() => {
        toast.error('Unable to share screen');
      });
    }
  }, [room, screenShare, sharing, user]);

  useEffect(() => {
    room && participants && participants.forEach((participant) => {
      participant.videoTracks.forEach((publication) => {
        if (publication?.track?.name === 'screenShare' && publication?.track?.isEnabled === true) {
          setSharing(true);
        }
      });
    });
  });

  useEffect(() => {
    if (participants.length <= 1) return;
    function getAudioLevels() {
      room && room.getStats().then((data) => {
        setAudioStats(data[0]);
      }).catch(() => { });
    }
    getAudioLevels();
    const interval = setInterval(() => getAudioLevels(), 100);
    return () => {
      clearInterval(interval);
    };
  }, [room, participants.length]);

  const getGridColumns = useCallback(() => {
    if (sharing) {
      if (participants.length < 4) {
        return (participants.length || 0) + 1;
      }
      return 5;
    }
    if (participants.length < 2) {
      return 1;
    }
    if (participants.length < 4) {
      return 2;
    }
    if (participants.length > 3 && participants.length < 6) {
      return 3;
    }
    return 4;
  }, [participants.length, sharing]);

  const getRowHeight = useCallback(() => {
    if (minimised) {
      if (participants.length === 0) {
        return '94%';
      }
      if (participants.length === 1) {
        return '47%';
      }
      if (participants.length === 2) {
        return '32%';
      }
      return '22%';
    }
    return '49%';
  }, [minimised, participants.length]);

  const displayCount = useCallback(() => {
    if (sharing && !minimised) {
      return participants.length > 4 ? 3 : 4;
    }
    if (minimised) {
      return (participants.length > 3) ? 2 : 3;
    }
    return participants.length > 7 ? 6 : 7;
  }, [minimised, participants.length, sharing]);

  const additionalParticipants = useCallback(() => {
    if (sharing && !minimised) { return 3; }
    return minimised ? 2 : 6;
  }, [minimised, sharing]);

  useEffect(() => {
    const participantDisplayCount = displayCount() - 1;
    const speakerIndex = participants.indexOf(dominantSpeaker);
    if (dominantSpeaker && speakerIndex >= participantDisplayCount) {
      setParticipants((prevParticipants) => prevParticipants.filter((p) => p !== dominantSpeaker));
      setParticipants((prevParticipants) => [dominantSpeaker, ...prevParticipants]);
    }
  }, [dominantSpeaker, participants, minimised, sharing, displayCount]);

  const copyCallLink = () => {
    if (window.navigator?.clipboard) {
      window.navigator.clipboard.writeText(`https://${user?.organisation.code || 'simpyldo'}.simplydo.co.uk/?call=${roomName}`).then(
        () => toast.success('Link copied to your clipboard'),
        () => toast.error('Unable to copy the link'),
      );
    } else {
      toast.error('Your browser does not support copying');
    }
  };

  const minimumUserCount = (sharing && 4) || minimised ? 3 : 6;
  return (
    <>
      <UI.VideoChatContainer minimised={minimised ? 1 : 0}>
        <HeaderDiv>
          {minimised
            ? <MaximiseIcon name='window maximize outline' onClick={() => setMinimised(false)} />
            : <>
              <HeaderButton onClick={() => setMinimised(true)} icon='window minimize' />
            </>
          }
        </HeaderDiv>
        {!localConnected
          && <Label circular style={{ backgroundColor: '#4f4c4c', position: 'absolute', zIndex: 'inherit' }} size='big'><span style={{ color: 'white', fontSize: 13, marginRight: 2 }}><Icon name='wifi' /> Network problems. Attempting to reconnect to the call.</span></Label>
        }
        <VideoGrid columns={minimised ? 1 : getGridColumns()} F minimised={minimised ? 1 : 0} style={{ height: (!minimised && '83vh') || participants.length > 0 ? '80vh' : '40vh' }}>
          <Grid.Row style={{ height: '75%', display: (!sharing || minimised) && 'none', padding: 3 }}>
            <SharingColumn sharing={sharing ? 1 : 0} width={16} >
              <ShareVideo sharing={sharing ? 1 : 0} ref={videoRefShare} autoPlay={true} />
            </SharingColumn>
          </Grid.Row>
          <Grid.Row style={{ height: (sharing && !minimised) ? '25%' : getRowHeight(), padding: 3 }}>
            {participants.length > 0 && participants.slice(0, displayCount()).map((participant) => (
              <GridColumn key={participant?.identity} >
                <Participant key={participant.sid} participant={participant} isMinimised={minimised} videoRefShare={videoRefShare} setSharing={setSharing} isSharing={sharing} dominantSpeaker={dominantSpeaker} audioStats={audioStats} />
              </GridColumn>
            ))}
            {room
              && <GridColumn>
                <Participant key={room.localParticipant.sid} participant={room.localParticipant} localCameraOff={cameraOff} videoRefShare={videoRefShare} setSharing={setSharing} isSharing={sharing} dominantSpeaker={dominantSpeaker} audioStats={audioStats} localMuted={muted} isMinimised={minimised} localUser={true} localTracks={localTracks} localNetworkQuality={localNetworkQuality} setLocalNetworkQuality={setLocalNetworkQuality} />
              </GridColumn>
            }
            {participants.length > minimumUserCount
              && <GridColumn>
                <PlaceholderContainer>
                  <UserAvatar>
                    <span>+{participants.length - additionalParticipants()}<UI.StyledIcon style={{ marginLeft: 5 }} name='users' /></span>
                  </UserAvatar>
                </PlaceholderContainer>
              </GridColumn>
            }
          </Grid.Row>
        </VideoGrid>
        <ActionsContainer minimised={minimised ? 1 : 0}>
          <UI.Audio toggleAudio={() => setMuted(!muted)} muted={muted} />
          <Popup
            content={'Your network connection is unstable. Turning off your camera may improve connection.'}
            open={localConnected && localNetworkQuality !== null && localNetworkQuality < 2 && !cameraOff}
            trigger={<Label onClick={() => setCameraOff((c) => !c)} style={{ backgroundColor: cameraOff ? '#d92525' : '#4f4c4c' }} circular size='big'><UI.StyledIcon size='small' name='video' /></Label>}
          />
          {!minimised
            && <Popup
              on='hover'
              content={'Screen sharing'}
              trigger={<Label onClick={() => shareScreen()} style={{ backgroundColor: sharing === user?._id ? '#d92525' : '#4f4c4c' }} circular size='big'><UI.StyledIcon size='small' name='television' /></Label>}
            />
          }
          {!minimised
            && <div style={{
              position: 'absolute', left: 5, bottom: 0, cursor: 'pointer',
            }}>
              <Popup
                content='This link will take users directly to the call. Only members of the chat will be able to join the call. You can add users to the chat in the chat settings.'
                trigger={<Label icon='copy' onClick={copyCallLink} content='Copy call link' style={{ color: 'white', backgroundColor: '#4f4c4c' }} />}
              />
            </div>
          }
          {!minimised
            && <Popup
              on='hover'
              style={{ cursor: 'pointer' }}
              content={
                <>
                  <p>{user?.profile.fullName}</p>
                  {participantList && participantList?.map((p) => <p key={p._id}>{p?.profile?.fullName}</p>)}
                </>
              }
              trigger={<Label circular style={{ backgroundColor: '#4f4c4c' }} size='big'><span style={{ color: 'white', fontSize: 13, marginRight: 2 }}>{participants.length + 1}</span><UI.StyledIcon size='small' name='users' /></Label>}
            />
          }
          <UI.Logout leaveRoom={leaveRoom} />
        </ActionsContainer>
      </UI.VideoChatContainer>
    </>
  );
};

const mapStateToProps = (state) => ({ user: state.user });
const RoomContainer = (connect(
  mapStateToProps,
)(Room));

export default RoomContainer;
